import {t} from "@lingui/core/macro";
import {StyleSheet, type CSSProperties} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {
    border,
    font,
    semanticColor,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingSmall,
    LabelLarge,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";

import Card from "./card.tsx";
import {
    CARD_HEIGHT,
    CARD_WIDTH,
    DESCRIPTION_CHARACTER_LIMIT,
    TITLE_CHARACTER_LIMIT,
} from "./constants.ts";
import Field from "./field.tsx";
import PlaceholderQuestion from "./placeholder-question.tsx";
import sharedStyles from "./shared-styles.ts";
import type {BlooketQuestion, QuestionSetAnimation} from "./types.ts";

function getAnimationStyle(
    animation: QuestionSetAnimation,
    index: number,
): CSSProperties {
    if (animation === "ADDING" && index === 0) {
        return sharedStyles.slidingIn;
    }
    return {};
}

interface Props {
    title: string;
    description: string;
    questions: BlooketQuestion[];
    cardHeight: number;
    cardWidth: number;
    setQuestions: (
        fn: (questions: BlooketQuestion[]) => BlooketQuestion[],
    ) => void;
    setTitle: (value: string) => void;
    setDescription: (value: string) => void;
    animation: QuestionSetAnimation;
}

const QuestionSet: React.FC<Props> = (props) => {
    const setNthCard =
        (index: number): ((card: BlooketQuestion) => void) =>
        (card: BlooketQuestion) => {
            props.setQuestions((current: BlooketQuestion[]) => [
                ...current.slice(0, index),
                card,
                ...current.slice(index + 1),
            ]);
        };

    const deleteNthCard =
        (index: number): (() => void) =>
        () => {
            props.setQuestions((current: BlooketQuestion[]) => [
                ...current.slice(0, index),
                ...current.slice(index + 1),
            ]);
        };

    return (
        <View>
            <View style={styles.setHeading}>
                <HeadingSmall>
                    <Field
                        text={props.title}
                        setText={props.setTitle}
                        style={styles.title}
                        maxLength={TITLE_CHARACTER_LIMIT}
                        ariaLabel={t`Question set title`}
                    />
                </HeadingSmall>
                <Strut size={spacing.xSmall_8} />
                <Field
                    text={props.description}
                    setText={props.setDescription}
                    style={styles.description}
                    maxLength={DESCRIPTION_CHARACTER_LIMIT}
                    ariaLabel={t`Question set description`}
                />
            </View>
            <Strut size={spacing.xSmall_8} />
            <View style={styles.questions}>
                <View style={styles.questionCountHeader}>
                    <View style={styles.count}>
                        <LabelLarge>{props.questions.length}</LabelLarge>
                    </View>
                    <View style={styles.questionCountLabel}>
                        <LabelSmall>{t`Questions`}</LabelSmall>
                    </View>
                </View>
                {props.questions.map((q: BlooketQuestion, i: number) => (
                    <Card
                        key={i}
                        card={q}
                        setCard={setNthCard(i)}
                        showActions={true}
                        cardHeight={props.cardHeight}
                        cardWidth={props.cardWidth}
                        onDelete={deleteNthCard(i)}
                        animationStyle={getAnimationStyle(props.animation, i)}
                    />
                ))}
                {props.questions.length > 1 ? null : (
                    <PlaceholderQuestion
                        cardHeight={props.cardHeight}
                        cardWidth={props.cardWidth}
                    />
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    questionCountHeader: {
        flexDirection: "row",
        display: "flex",
        width: CARD_WIDTH,
    },
    questionCountLabel: {
        justifyContent: "center",
        marginInlineStart: spacing.xxSmall_6,
    },
    count: {
        borderRadius: border.radius.radius_040,
        backgroundColor: semanticColor.core.background.neutral.default,
        color: semanticColor.core.foreground.knockout.default,
        padding: spacing.xxxxSmall_2,
        margin: spacing.xxxSmall_4,
        minInlineSize: spacing.large_24,
        textAlign: "center",
    },
    setHeading: {
        flexGrow: 1,
    },
    questions: {
        padding: spacing.xSmall_8,
        borderWidth: "thin",
        borderStyle: "solid",
        borderColor: semanticColor.core.border.neutral.subtle,
        borderRadius: border.radius.radius_040,
        rowGap: spacing.xSmall_8,
        minBlockSize: CARD_HEIGHT,
    },
    title: {
        fontSize: font.size.large,
        fontWeight: "bold",
    },
    description: {
        fontSize: font.size.small,
    },
});

export default QuestionSet;
